import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import type { AuthProviderProps } from '../types';
import type { AuthContextType, AuthTokens } from '@/types/auth';
import { extractTokensFromUrl, cleanUrlParams, areTokensValid, decodeJWT } from '@/services/auth/jwtUtils';
import { authApi } from '@/services/api/auth/authApi';
import { store } from '@/store';
import { extractUserAccountData, validateUserData } from '@/features/auth/utils/userAccountHelpers';

const AuthContext = createContext<AuthContextType | null>(null);

/**
 * AuthProvider provides authentication context using JWT tokens from URL parameters.
 * It handles token extraction, validation, and user account creation.
 *
 * @param {AuthProviderProps} props - The provider props.
 * @returns {JSX.Element} The provider-wrapped children.
 */
export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [tokens, setTokens] = useState<AuthTokens | null>(() => {
    // Try to load tokens from localStorage on initialization
    const storedTokens = localStorage.getItem('auth_tokens');
    if (storedTokens) {
      try {
        const parsedTokens = JSON.parse(storedTokens);
        return areTokensValid(parsedTokens) ? parsedTokens : null;
      } catch {
        localStorage.removeItem('auth_tokens');
        return null;
      }
    }
    return null;
  });

  const [user, setUser] = useState<unknown | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const login = useCallback(async (): Promise<void> => {
    // This is handled by the LoginForm component redirecting to Moneta
    throw new Error('Login should be handled by redirecting to the authentication service');
  }, []);

  const logout = useCallback(async (): Promise<void> => {
    try {
      setTokens(null);
      setUser(null);
      setError(null);
      localStorage.removeItem('auth_tokens');
      localStorage.removeItem('auth_user');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Logout failed');
    }
  }, []);

  // Handle tokens from URL parameters on mount
  useEffect(() => {
    const handleUrlTokens = async () => {
      try {
        setIsLoading(true);
        const urlTokens = extractTokensFromUrl();

        if (urlTokens) {
          // Validate tokens
          if (!areTokensValid(urlTokens)) {
            throw new Error('Invalid tokens received from authentication service');
          }

          // Extract user data from ID token
          const idTokenPayload = decodeJWT(urlTokens.idToken!);

          // Validate user data structure
          if (!validateUserData({ profile: idTokenPayload, access_token: urlTokens.accessToken })) {
            throw new Error('Invalid user data received from authentication service');
          }

          // Extract user account data
          const requestData = extractUserAccountData({
            profile: idTokenPayload,
            access_token: urlTokens.accessToken
          });

          // Save user account via API
          await store.dispatch(
            authApi.endpoints.saveUserAccount.initiate({
              data: requestData,
              token: urlTokens.accessToken,
            })
          ).unwrap();

          // Store tokens and user data
          setTokens(urlTokens);
          setUser(idTokenPayload);
          localStorage.setItem('auth_tokens', JSON.stringify(urlTokens));
          localStorage.setItem('auth_user', JSON.stringify(idTokenPayload));

          // Clean URL parameters
          cleanUrlParams();
        }
      } catch (err) {
        console.error('Failed to handle authentication callback:', err);
        setError(err instanceof Error ? err.message : 'Authentication failed');

        // Clean up on error
        setTokens(null);
        setUser(null);
        localStorage.removeItem('auth_tokens');
        localStorage.removeItem('auth_user');
        cleanUrlParams();
      } finally {
        setIsLoading(false);
      }
    };

    // Load user from localStorage if tokens exist
    if (tokens && !user) {
      const storedUser = localStorage.getItem('auth_user');
      if (storedUser) {
        try {
          setUser(JSON.parse(storedUser));
        } catch {
          localStorage.removeItem('auth_user');
        }
      }
      setIsLoading(false);
    } else if (!tokens) {
      handleUrlTokens();
    } else {
      setIsLoading(false);
    }
  }, [tokens, user]);

  const contextValue: AuthContextType = {
    user,
    tokens,
    isAuthenticated: !!tokens && !!user && areTokensValid(tokens),
    isLoading,
    error,
    login,
    logout,
  };

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
